import { styled } from '@mui/material/styles';

import { TOGGLE_BOTTOM_CSS, TOGGLE_FILL, toggleMetrics } from '../../components/viewToggleMetrics';
import { BRAND } from '../../shared/theme/paletteDark';

/**
 * What the Map layout draws over the map's edges (contract §15): the map runs
 * full-bleed under the header and the bottom band, so a gradient from the
 * page's mocha at each edge to nothing over ~120px keeps the controls there
 * readable. And the recentre button: beside the phone's layout pill, in the
 * map's bottom-right corner everywhere else.
 *
 * `phone` is fixed to the screen, as the pill it sits beside is. `desktop` is
 * placed in the map's own column, which is its positioned parent.
 */

/** How far each gradient reaches into the map. */
export const MAP_EDGE_FADE = 120;

const MOCHA = BRAND.mocha;

const Edge = styled('div', { shouldForwardProp: (p) => p !== '$edge' && p !== '$fixed' })<{
  $edge: 'top' | 'bottom';
  $fixed: boolean;
}>(({ $edge, $fixed }) => ({
  position: $fixed ? 'fixed' : 'absolute',
  left: 0,
  right: 0,
  [$edge]: 0,
  height: MAP_EDGE_FADE,
  // Over the tiles and Leaflet's panes, under the header, the band and every sheet.
  zIndex: 2,
  pointerEvents: 'none',
  background: `linear-gradient(to ${$edge === 'top' ? 'bottom' : 'top'}, ${MOCHA} 0%, rgba(54,47,42,0.55) 45%, rgba(54,47,42,0) 100%)`,
}));

/** The phone's size: the layout pill's height, so the two read as one band. */
const PHONE_D = toggleMetrics([]).height;
const DESKTOP_D = 44;

const Recentre = styled('button', { shouldForwardProp: (p) => p !== '$phone' })<{ $phone: boolean }>(({ $phone }) => ({
  appearance: 'none',
  position: $phone ? 'fixed' : 'absolute',
  right: 16,
  bottom: $phone ? TOGGLE_BOTTOM_CSS : 28,
  // The phone's sits with the layout pill, over the sheets; the desktop's over the map only.
  zIndex: $phone ? 1270 : 3,
  width: $phone ? PHONE_D : DESKTOP_D,
  height: $phone ? PHONE_D : DESKTOP_D,
  display: 'grid',
  placeItems: 'center',
  border: 'none',
  borderRadius: 999,
  padding: 0,
  cursor: 'pointer',
  background: $phone ? TOGGLE_FILL.phone : 'rgba(36,31,28,0.82)',
  color: BRAND.parchment,
  boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
  WebkitTapHighlightColor: 'transparent',
  transition: 'background-color 120ms ease, opacity 120ms ease',
  '@media (hover: hover)': { '&:hover': { background: 'rgba(11,9,9,0.9)' } },
  '&:focus-visible': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: 2 },
  '&[aria-busy="true"]': { opacity: 0.6, cursor: 'progress' },
}));

/** A navigation arrow, outlined, pointing up and to the right. */
function Arrow({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M20.5 3.5 3.8 10.4c-.6.3-.6 1.2.1 1.4l6.6 1.8 1.8 6.6c.2.7 1.1.7 1.4.1z"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface Props {
  variant: 'phone' | 'desktop';
  /** Pans the map to the device; asks for the position the first time. */
  onRecenter: () => void;
  /** Waiting on the position. */
  locating?: boolean;
  /** False while a phone's callout sheet is up: the button is the map's, not the sheet's. */
  showRecentre?: boolean;
}

export default function MapChrome({ variant, onRecenter, locating = false, showRecentre = true }: Props) {
  const phone = variant === 'phone';
  return (
    <>
      <Edge $edge="top" $fixed={phone} />
      <Edge $edge="bottom" $fixed={phone} />
      {showRecentre && (
        <Recentre
          type="button"
          $phone={phone}
          aria-label="Show my location"
          title={phone ? undefined : 'Show my location'}
          aria-busy={locating || undefined}
          onClick={onRecenter}
        >
          <Arrow size={phone ? 24 : 20} />
        </Recentre>
      )}
    </>
  );
}
